import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { fmtDate } from "@/lib/format";
import { openFile } from "@/lib/fileViewer";
import { eur } from "@/components/enrollment/FormParts";
import { FileDown, Paperclip, Check, X, Trash2 } from "lucide-react";

const tok = () => encodeURIComponent(localStorage.getItem("token"));
const yn = (v) => (v ? "Sì" : "No");

function Row({ l, v }) {
  return <div className="grid grid-cols-[150px_1fr] gap-2 text-sm py-1 border-b border-stone-100"><span className="text-stone-500">{l}</span><span className="break-words">{v || "—"}</span></div>;
}

export function EnrollmentDetailDialog({ e, onClose }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const [reason, setReason] = useState("");
  const done = (msg) => { toast.success(msg); qc.invalidateQueries({ queryKey: ["enrollments"] }); onClose(); };
  const err = (x) => toast.error(x.response?.data?.detail || "Errore");
  const approve = useMutation({ mutationFn: async () => (await api.post(`/enrollments/${e.id}/approve`)).data,
    onSuccess: (d) => { done(`Studente creato (${d.matricola})`); nav(`/modulare/studenti/${d.student_id}`); }, onError: err });
  const reject = useMutation({ mutationFn: async () => api.post(`/enrollments/${e.id}/reject`, { reason }), onSuccess: () => done("Iscrizione rifiutata"), onError: err });
  const del = useMutation({ mutationFn: async () => api.delete(`/enrollments/${e.id}`), onSuccess: () => done("Iscrizione eliminata"), onError: err });
  if (!e) return null;
  const a = e.amount || {};

  return (
    <Dialog open={!!e} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="enr-detail-dialog">
        <DialogHeader><DialogTitle>{e.last_name} {e.first_name}</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="rounded-full" data-testid="enr-pdf-btn"
            onClick={() => { onClose(); openFile(`${API_BASE}/enrollments/${e.id}/pdf?auth=${tok()}`, `iscrizione_${e.last_name}.pdf`); }}><FileDown size={14} className="mr-1"/> {e.accept_regulation ? "Modulo PDF firmato" : "PDF pre-iscrizione"}</Button>
          {(e.files || []).map((f) => (
            <Button key={f.id} variant="outline" size="sm" className="rounded-full" data-testid={`enr-file-${f.id}`}
              onClick={() => { onClose(); openFile(`${API_BASE}/enrollments/files/${f.id}/download?auth=${tok()}`, f.original_filename); }}><Paperclip size={13} className="mr-1"/> {f.label}</Button>
          ))}
        </div>
        <div>
          <Row l="Nato/a" v={`${e.birth_place}${e.birth_province ? ` (${e.birth_province})` : ""}, ${fmtDate(e.date_of_birth)}`}/>
          <Row l="Cittadinanza" v={e.nationality === "estera" ? `Estera · ${e.country || "—"}` : "Italiana"}/>
          <Row l="Codice fiscale" v={e.codice_fiscale || (e.nationality === "estera" ? "Non in possesso" : null)}/>
          <Row l="Residenza" v={`${e.address}, ${e.cap} ${e.city}${e.province ? ` (${e.province})` : ""}`}/>
          <Row l="Domicilio" v={e.domicile}/>
          <Row l="Contatti" v={`${e.phone} · ${e.email}`}/>
          <Row l="Titolo di studio" v={e.education}/>
          <Row l="Chiesa" v={e.chiesa_provenienza}/>
          <Row l="Pagamento" v={`${e.payment_plan === "unica" ? "Unica soluzione" : e.payment_plan === "mensile" ? `Mensile (${a.installments} rate)` : `${a.installments} rate`} · bonifico · totale ${eur(a.total)} · primo versamento ${eur(a.first_payment)}`}/>
          {e.couple && <Row l="In coppia con" v={e.spouse_name}/>}
          <Row l="Esigenze alimentari" v={e.has_food_needs ? `${e.food_needs || ""} ${e.food_notes || ""}` : "No"}/>
          <Row l="Apprendimento" v={e.has_learning_needs ? `${e.learning_needs || ""} ${e.learning_tools || ""}` : "No"}/>
          <Row l="Consensi" v={`Salute ${yn(e.consent_health)} · DSA ${yn(e.consent_learning)} · Chiesa ${yn(e.consent_church)} · WhatsApp ${yn(e.consent_whatsapp)}${e.whatsapp_number ? ` (${e.whatsapp_number})` : ""} · Foto/video ${yn(e.consent_media)}`}/>
          <Row l="Note documenti" v={e.docs_notes}/>
          <Row l="Info aggiuntive" v={e.additional_info}/>
          <Row l="Inviata" v={`${e.sign_place ? `${e.sign_place}, ` : ""}${fmtDate(e.created_at)}`}/>
          {e.decided_by_name && <Row l="Decisione" v={`${e.status} da ${e.decided_by_name}, ${fmtDate(e.decided_at)}${e.reject_reason ? ` — ${e.reject_reason}` : ""}`}/>}
        </div>
        {e.status !== "approvata" && (
          <div className="space-y-3 pt-2">
            <Button onClick={() => approve.mutate()} disabled={approve.isPending} className="w-full bg-[#1C352D] hover:bg-[#264A3E]" data-testid="enr-approve-btn">
              <Check size={15} className="mr-1"/> Approva e crea studente modulare (I anno)
            </Button>
            {e.status === "in_attesa" && <div className="flex gap-2">
              <Textarea value={reason} onChange={(x) => setReason(x.target.value)} placeholder="Motivo (facoltativo)" rows={1} data-testid="enr-reject-reason"/>
              <Button variant="outline" onClick={() => reject.mutate()} disabled={reject.isPending} data-testid="enr-reject-btn"><X size={14} className="mr-1"/> Rifiuta</Button>
            </div>}
            <Button variant="ghost" size="sm" className="text-red-700" onClick={() => window.confirm("Eliminare definitivamente questa iscrizione?") && del.mutate()} data-testid="enr-delete-btn">
              <Trash2 size={13} className="mr-1"/> Elimina
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
